/** @jsx jsx */
import { Box } from '@balance-web/box';
import type { ColorScheme } from '@balance-web/button';
import { Button } from '@balance-web/button';
import type { DataAttributeMap } from '@balance-web/core';
import { jsx } from '@balance-web/core';
import { Heading } from '@balance-web/heading';
import { useTheme } from '@balance-web/theme';
import { makeId, useId } from '@balance-web/utils';
import { Text } from '@balance-web/text';
import { Flex } from '@balance-web/flex';

import type { ModalSize } from './size';
import { useModalWidth } from './size';
import { DialogBase } from './DialogBase';

type Action = {
  action: () => void;
  label: string;
  data?: DataAttributeMap;
};

type AlertDialogProps = {
  actions: {
    block?: boolean /** make action buttons full width */;
    cancel?: Action;
    confirm: Action & { loading?: boolean };
    secondaryConfirm?: Action & {
      loading?: boolean;
      colorScheme?: ColorScheme;
    };
  };
  id?: string;
  isOpen: boolean;
  children: React.ReactNode;
  title: string;
  tone?: 'critical' | 'primary';
  size?: ModalSize;
  autoFocus?: boolean;
};

export const AlertDialog = ({
  actions,
  isOpen,
  children,
  title,
  id,
  tone = 'primary',
  size = 'small',
  autoFocus = true,
}: AlertDialogProps) => {
  const { cancel, confirm, secondaryConfirm } = actions;
  const theme = useTheme();
  const instanceId = useId(id);
  const headingId = makeId(instanceId, 'heading');

  const modalWidth = useModalWidth(size);

  // call the "cancel" action on `Esc` key press (if it exists), or the "confirm" action if there is no "cancel" action.
  const onClose = () => {
    if (actions.cancel) {
      actions.cancel.action();
    } else {
      actions.confirm.action();
    }
  };

  const hasSecondaryConfirm = secondaryConfirm && secondaryConfirm.label !== '';

  return (
    <DialogBase
      aria-labelledby={headingId}
      autoFocus={autoFocus}
      elevation="modal"
      id={instanceId}
      isOpen={isOpen}
      width={modalWidth}
      onClose={onClose}
    >
      <Box
        css={{ display: 'flex', flexDirection: 'column', overflowY: 'auto' }}
        paddingX="xlarge"
      >
        <Box paddingBottom="large" paddingTop="xlarge">
          <Heading id={headingId} level="3">
            {title}
          </Heading>
        </Box>

        <Box paddingY="large">
          <Text color="textPrimary">{children}</Text>
        </Box>

        <div
          css={{
            display: 'flex',
            justifyContent: 'start',
            flexWrap: 'wrap',
            flexDirection: actions.block ? 'column-reverse' : 'row',
            paddingTop: theme.spacing.medium,
            paddingBottom: theme.spacing.xlarge,
            gap: theme.spacing.large,
          }}
        >
          {cancel ? (
            <Button
              key={cancel.label}
              block={actions.block}
              colorScheme="tertiary"
              data={cancel.data}
              disabled={confirm.loading || secondaryConfirm?.loading}
              id={makeId(instanceId, 'cancel')}
              label={cancel.label}
              size="small"
              variant="filled"
              onClick={cancel.action}
            />
          ) : null}
          <Flex flex="1" justifyContent="end">
            {hasSecondaryConfirm ? (
              <Button
                key={secondaryConfirm.label}
                block={actions.block}
                colorScheme={secondaryConfirm.colorScheme || 'critical'}
                data={secondaryConfirm.data}
                id={makeId(instanceId, 'secondaryConfirm')}
                label={secondaryConfirm.label}
                loading={secondaryConfirm.loading}
                size="small"
                variant="outline"
                onClick={secondaryConfirm.action}
              />
            ) : null}

            <Button
              key={confirm.label}
              block={actions.block}
              colorScheme={tone}
              css={{
                marginLeft: actions.block ? 0 : theme.spacing.medium,
                marginBottom: actions.block ? theme.spacing.medium : 0,
              }}
              id={makeId(instanceId, 'confirm')}
              data={confirm.data}
              label={confirm.label}
              loading={confirm.loading}
              size="small"
              onClick={confirm.action}
            />
          </Flex>
        </div>
      </Box>
    </DialogBase>
  );
};
